import { observer } from "mobx-react-lite";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useEditorConfig } from "@/components/ContentArea/editor-config-context";
import ReleaseLabel from "@/components/ReleaseLabel";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { useUserToken } from "@/context/auth-context";
import { usePromptVersions } from "@/queries";
import { usePromptLabels } from "@/queries/prompt-labels";

interface VersionLabelSelectorProps {
  promptId?: number;
  currentVersion?: number;
  currentLabel?: string;
  hasChanged?: boolean;
  onChange?: (version: number | undefined, label: string | undefined) => void;
  isReadOnly?: boolean;
}

interface Label {
  id: number;
  name: string;
}

interface Version {
  id: number;
  number: number;
}

const LABEL_PREFIX = "label:";
const VERSION_PREFIX = "version:";
const LATEST_VALUE = "latest";

const VersionLabelSelector = observer(
  ({
    promptId,
    currentVersion,
    currentLabel,
    hasChanged = false,
    onChange,
    isReadOnly = false,
  }: VersionLabelSelectorProps) => {
    const { workspaceId } = useParams();
    const userToken = useUserToken();
    const { disableInteractive } = useEditorConfig();

    const { data: versionsData } = usePromptVersions(userToken, {
      workspaceId: Number(workspaceId),
      promptRegistryId: promptId,
      page: 1,
      perPage: 100,
    });

    const { data: labelsData } = usePromptLabels({
      userToken,
      workspace_id: Number(workspaceId),
      prompt_id: promptId,
      enabled: !!promptId,
    });

    const versions: Version[] = versionsData?.pages?.[0]?.prompt_versions || [];
    const labels: Label[] = labelsData?.prompt_labels || [];

    const displayValue = useMemo(() => {
      if (hasChanged) {
        return "Latest";
      }
      if (currentLabel) {
        return currentLabel;
      }
      if (currentVersion) {
        return `Version ${currentVersion}`;
      }
      return "Latest";
    }, [currentLabel, currentVersion, hasChanged]);

    const selectValue = useMemo((): string => {
      if (hasChanged) {
        return LATEST_VALUE;
      }
      if (currentLabel) {
        return `${LABEL_PREFIX}${currentLabel}`;
      }
      if (currentVersion) {
        return `${VERSION_PREFIX}${currentVersion}`;
      }
      return LATEST_VALUE;
    }, [currentLabel, currentVersion, hasChanged]);

    const handleValueChange = useCallback(
      (value: string): void => {
        if (!onChange) {
          return;
        }

        if (value === LATEST_VALUE) {
          onChange(undefined, undefined);
        } else if (value.startsWith(LABEL_PREFIX)) {
          const labelName = value.slice(LABEL_PREFIX.length);
          onChange(undefined, labelName);
        } else if (value.startsWith(VERSION_PREFIX)) {
          const versionNum = parseInt(value.slice(VERSION_PREFIX.length), 10);
          onChange(versionNum, undefined);
        }
      },
      [onChange],
    );

    const canInteract = useMemo(
      () => !disableInteractive && !isReadOnly && !!promptId,
      [disableInteractive, isReadOnly, promptId],
    );

    if (!canInteract) {
      return (
        <span className="rounded-md bg-black/5 px-2 py-1.5 text-xs font-medium text-white">
          {displayValue}
        </span>
      );
    }

    return (
      <Select value={selectValue} onValueChange={handleValueChange}>
        <SelectTrigger className="h-auto max-w-[200px] gap-1 rounded-md border-0 bg-black/5 px-2 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-black/10 focus:ring-1 focus:ring-white">
          <OverflowDisplayTooltip content={displayValue}>
            <span className="truncate">{displayValue}</span>
          </OverflowDisplayTooltip>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={LATEST_VALUE}>Latest</SelectItem>

          {labels.length > 0 &&
            labels.map(label => (
              <SelectItem key={`label-${label.id}`} value={`${LABEL_PREFIX}${label.name}`}>
                <ReleaseLabel label={label} readOnly showDeleteButton={false} />
              </SelectItem>
            ))}

          {versions.length > 0 &&
            versions.map(version => (
              <SelectItem
                key={`version-${version.id}`}
                value={`${VERSION_PREFIX}${version.number}`}
              >
                Version {version.number}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    );
  },
);

export default VersionLabelSelector;
